import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { 
  MagnifyingGlassIcon, 
  MapPinIcon, 
  ClockIcon, 
  ChevronDownIcon,
  ChevronRightIcon,
  TagIcon,
  HomeIcon,
  TruckIcon,
  DevicePhoneMobileIcon,
  WrenchScrewdriverIcon,
  BriefcaseIcon,
  SparklesIcon,
  UserIcon,
  PuzzlePieceIcon,
  UsersIcon,
  StarIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  ShieldCheckIcon,
  ClockIcon as Clock24Icon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { adsApi, categoriesApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getImageUrl } from '../utils/imageUtils';
import CitiesModal from '../components/CitiesModal';
import '../styles/promotional-effects.css';
import axios from 'axios';

const BASE_URL = window.location.hostname === 'localhost' ? 'https://localhost:7000/api' : 'https://dealden.ca/api';

const HomePage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  // Search state
  const [quickSearchTerm, setQuickSearchTerm] = useState('');
  const [quickSearchCities, setQuickSearchCities] = useState<string[]>([]);
  const [quickSearchCategory, setQuickSearchCategory] = useState('');
  
  // Cities modal state
  const [isCitiesModalOpen, setIsCitiesModalOpen] = useState(false);

  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.getAll()
  });

  const { data: trendingAds, isLoading: adsLoading } = useQuery({
    queryKey: ['trending-ads'],
    queryFn: () => adsApi.search({ page: 1, pageSize: 6, sortBy: 'date_new' })
  });

  const { data: statistics, isLoading: isLoadingStats } = useQuery({
    queryKey: ['public-statistics'],
    queryFn: async () => {
      try {
        const response = await axios.get(`${BASE_URL}/statistics/public`);
        return response.data;
      } catch (error: any) {
        console.error('Error fetching statistics:', error.response?.data || error.message);
        return null;
      }
    },
    staleTime: 2 * 60 * 1000, // Data is considered fresh for 2 minutes
    refetchInterval: 5 * 60 * 1000, // Automatically refetch every 5 minutes
  });

  // Icon mapping function
  const getIconComponent = (iconName: string | undefined) => {
    if (!iconName) return TagIcon;
    
    const iconMap: { [key: string]: React.ComponentType<{ className?: string }> } = {
      'home': HomeIcon,
      'car': TruckIcon,
      'electronics': DevicePhoneMobileIcon,
      'tools': WrenchScrewdriverIcon,
      'job': BriefcaseIcon,
      'services': SparklesIcon,
      'fashion': UserIcon,
      'sports': PuzzlePieceIcon,
      'books': UsersIcon,
      'pets': UserIcon,
      'garden': SparklesIcon,
      'furniture': HomeIcon
    };
    
    return iconMap[iconName.toLowerCase()] || TagIcon;
  };

  const handleQuickSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const searchParams = new URLSearchParams();
    if (quickSearchTerm) searchParams.set('q', quickSearchTerm);
    if (quickSearchCities.length > 0) searchParams.set('city', quickSearchCities.join(','));
    if (quickSearchCategory) searchParams.set('category', quickSearchCategory);
    navigate(`/search?${searchParams.toString()}`);
  };

  // Cities modal handlers
  const openCitiesModal = () => {
    setIsCitiesModalOpen(true);
  };

  const closeCitiesModal = () => {
    setIsCitiesModalOpen(false);
  };

  const handleCitiesSelect = (selectedCities: string[]) => {
    setQuickSearchCities(selectedCities);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return `${Math.ceil(diffDays / 30)} months ago`;
  };

  const stats = [
    { 
      icon: TagIcon, 
      value: statistics?.activeAds !== undefined ? `${statistics.activeAds.toLocaleString()}+` : '0+', 
      label: 'Active Ads', 
      color: 'text-red-600' 
    },
    { 
      icon: UsersIcon, 
      value: statistics?.happyUsers !== undefined ? `${statistics.happyUsers.toLocaleString()}+` : '0+', 
      label: 'Happy Users', 
      color: 'text-red-500' 
    },
    { 
      icon: ArrowTrendingUpIcon, 
      value: statistics?.dailyAds !== undefined ? `${statistics.dailyAds.toLocaleString()}+` : '0+', 
      label: 'Daily Ads', 
      color: 'text-red-700' 
    },
    { 
      icon: StarIcon, 
      value: statistics?.userRating !== undefined ? `${statistics.userRating}/5` : '4.8/5', 
      label: 'User Rating', 
      color: 'text-red-600' 
    }
  ];

  const features = [
    {
      icon: ShieldCheckIcon,
      title: 'High Security',
      description: 'All ads are reviewed and verified',
      color: 'bg-red-50 text-red-600'
    },
    {
      icon: Clock24Icon,
      title: 'Quick Response',
      description: 'Direct contact with sellers',
      color: 'bg-red-50 text-red-700'
    },
    {
      icon: CurrencyDollarIcon,
      title: 'Completely Free',
      description: 'Free posting and usage',
      color: 'bg-red-50 text-red-800'
    }
  ];

  const howItWorks = [
    {
      icon: '📝',
      title: 'Post Your Ad',
      description: 'Easily post your ad with photos and complete descriptions',
      color: 'bg-red-50 text-red-600'
    },
    {
      icon: '👥',
      title: 'Attract Buyers',
      description: 'Thousands of people view your ads daily',
      color: 'bg-red-50 text-red-700'
    },
    {
      icon: '💰',
      title: 'Successful Deal',
      description: 'Connect with buyers and complete your transactions',
      color: 'bg-red-50 text-red-800'
    }
  ];

  return (
    <main className="min-h-screen bg-white" role="main" dir="ltr">
      {/* Modern Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            {/* Hero Content */}
            <div className="lg:col-span-7 text-center lg:text-left">
              <div className="mb-8">
                <span className="inline-flex items-center bg-white/80 backdrop-blur-sm text-gray-700 px-6 py-3  rounded-full text-sm font-medium shadow-lg">
                                      <SparklesIcon className="h-4 w-4 mr-2" />
                  Canada's Trusted Buy & Sell Platform
                </span>
              </div>
              
              <h1 className="text-5xl lg:text-7xl font-semibold text-gray-900 mb-8 leading-tight">
                 Canada's 
                <span className="bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent"> Online</span>
                <br />Marketplace
              </h1>
              
              <p className="text-xl text-gray-600 mb-12 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Find anything you need or offer your products to millions of people
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12">
                <Link 
                  to="/search"
                  className="group bg-gradient-to-r from-red-600 to-red-700 text-white px-8 py-4 rounded-2xl font-semibold hover:from-red-700 hover:to-red-800 transition-all duration-300 inline-flex items-center justify-center shadow-2xl transform hover:-translate-y-1"
                >
                  <MagnifyingGlassIcon className="h-5 w-5 mr-3" />
                  Browse Ads
                </Link>
                <Link 
                  to="/post-ad"
                  className="bg-white border-2 border-red-200 text-red-700 px-8 py-4 rounded-2xl font-semibold hover:border-red-300 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                >
                  Post Free Ad
                </Link>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {isLoadingStats ? (
                  [1, 2, 3, 4].map((index) => (
                    <div key={index} className="text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white shadow-lg mb-3 animate-pulse">
                        <div className="h-6 w-6 bg-gray-200 rounded"></div>
                      </div>
                      <div className="h-8 bg-gray-200 rounded mb-2 animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  ))
                ) : (
                  stats.map((stat, index) => {
                    const IconComponent = stat.icon;
                    return (
                      <div key={index} className="text-center">
                        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full bg-white shadow-lg mb-3 ${stat.color}`}>
                          <IconComponent className="h-6 w-6" />
                        </div>
                        <div className="text-2xl font-semibold text-gray-900">{stat.value}</div>
                        <div className="text-sm text-gray-600">{stat.label}</div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Search Card */}
            <div className="lg:col-span-5">
              <div className="bg-white/95 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/50 p-8">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-2">Smart Search</h2>
                  <p className="text-gray-600">Find exactly what you're looking for</p>
                </div>
                
                <form className="space-y-6" onSubmit={handleQuickSearch}>
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Product or service name..."
                      value={quickSearchTerm}
                      onChange={(e) => setQuickSearchTerm(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-red-500 focus:bg-white transition-all duration-300"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="relative">
                      <MapPinIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            <input
                        type="text"
                         placeholder="Select location"
                         value={quickSearchCities.length > 0 ? `${quickSearchCities.length} cities selected` : ''}
                         onClick={openCitiesModal}
                         readOnly
                         className="w-full pl-12 pr-4 py-4 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-red-500 focus:bg-white transition-all duration-300 cursor-pointer hover:bg-white"
                      />
                    </div>
                     
                     <div className="relative">
                       <TagIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                       <select
                         value={quickSearchCategory}
                         onChange={(e) => setQuickSearchCategory(e.target.value)}
                         className="w-full pl-12 pr-12 py-4 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-red-500 focus:bg-white transition-all duration-300 appearance-none"
                         style={{ backgroundImage: 'none' }}
                       >
                        <option value="">Category</option>
                        {categories?.data?.filter(cat => !cat.parentCategoryId).map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                      <ChevronDownIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                  
                                     <button
                     type="submit"
                     className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white py-4 px-6 rounded-xl hover:from-red-700 hover:to-red-800 font-semibold transition-all duration-300 shadow-lg transform hover:-translate-y-1"
                   >
                     Search
                   </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="text-center group">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl ${feature.color} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-semibold text-gray-900 mb-4">Popular Categories</h2>
            <p className="text-xl text-gray-600">Choose and explore</p>
          </div>
          
                     <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
             {categories?.data?.filter(cat => !cat.parentCategoryId).slice(0, 12).map((category, index) => {
               const IconComponent = getIconComponent(category.iconUrl);
               return (
                 <Link
                   key={category.id}
                   to={`/search?category=${category.id}`}
                   className="group block text-center p-6 bg-white rounded-2xl shadow-sm hover:shadow-xl border border-gray-100 hover:border-red-200 transition-all duration-300 transform hover:-translate-y-2"
                 >
                   <div className="w-16 h-16 bg-gradient-to-br from-red-50 to-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:from-red-100 group-hover:to-red-200 transition-all duration-300">
                     <IconComponent className="h-8 w-8 text-gray-600 group-hover:text-red-600 transition-colors duration-300" />
                   </div>
                   <h3 className="font-semibold text-gray-900 text-sm group-hover:text-red-600 transition-colors duration-300">
                     {category.name}
                   </h3>
                 </Link>
               );
             })}
           </div>
          
                     <div className="text-center mt-12">
                         <Link 
              to="/search" 
              className="inline-flex items-center bg-gradient-to-r from-red-600 to-red-700 text-white px-8 py-3 rounded-xl hover:from-red-700 hover:to-red-800 font-semibold transition-all duration-300"
            >
              View All Categories
              <ChevronRightIcon className="h-5 w-5 ml-2" />
            </Link>
           </div>
        </div>
      </section>

      {/* Trending Ads Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-16">
            <div>
              <h2 className="text-4xl font-semibold text-gray-900 mb-4">Popular Ads</h2>
              <p className="text-xl text-gray-600">Latest and most popular ads</p>
            </div>
                                     <Link 
              to="/search" 
              className="hidden md:inline-flex items-center bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-3 rounded-xl hover:from-red-700 hover:to-red-800 font-semibold transition-all duration-300"
            >
              View All
              <ChevronRightIcon className="h-5 w-5 ml-2" />
            </Link>
          </div>
          
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {trendingAds?.data?.ads?.slice(0, 6).map((ad, index) => {
              // Special styling for highlighted ads
              const getAdStyling = () => {
                if (ad.isHighlighted) {
                  return "group block bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl shadow-xl border-2 border-red-200 overflow-hidden hover:shadow-2xl hover:border-red-300 transition-all duration-300 transform hover:-translate-y-3 hover:scale-105 shimmer-effect glow-highlighted";
                }
                return "group block bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-2xl hover:border-red-200 transition-all duration-300 transform hover:-translate-y-2";
              };

              return (
              <Link
                key={ad.id}
                to={`/ads/${ad.slug || ad.id}`}
                className={getAdStyling()}
              >
                 <div className="relative overflow-hidden">
                   <img
                     src={(() => {
                       const imageUrl = getImageUrl(ad.primaryImageUrl);
                       console.log('HomePage - Ad:', ad.id, 'Title:', ad.title, 'primaryImageUrl (raw):', ad.primaryImageUrl, 'getImageUrl result:', imageUrl);
                       return imageUrl;
                     })()}
                     alt={ad.title}
                     className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                   />
                   <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                   
                   {/* Promotional badges */}
                   <div className="absolute top-3 right-3 flex flex-col gap-2">
                     {ad.isUrgent && (
                       <span className="bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded-lg animate-pulse">
                         Urgent
                       </span>
                     )}
                     {ad.isHighlighted && (
                       <span className="bg-gradient-to-r from-red-600 to-orange-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-lg animate-pulse neon-text">
                         ⭐ Featured
                       </span>
                     )}
                     {ad.isFeatured && (
                       <span className="bg-red-700 text-white text-xs font-semibold px-2 py-1 rounded-lg">
                         Special
                       </span>
                     )}
                   </div>
                  
                  <div className="absolute bottom-3 left-3">
                    <span className={`${ad.isHighlighted 
                      ? 'bg-gradient-to-r from-red-600 to-orange-500 text-white shadow-lg border-2 border-white' 
                      : 'bg-white/95 backdrop-blur-sm text-gray-900'} text-lg font-semibold px-3 py-1 rounded-lg`}>
                      ${ad.price.toLocaleString()}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                                     <h3 className="font-semibold text-gray-900 text-lg mb-3 line-clamp-2 group-hover:text-red-600 transition-colors duration-300">
                     {ad.title}
                   </h3>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center">
                      <MapPinIcon className="h-4 w-4 mr-1" />
                      <span>{ad.city}</span>
                    </div>
                    <div className="flex items-center">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      <span>{formatDate(ad.datePosted)}</span>
                    </div>
                  </div>
                </div>
              </Link>
              );
            })}
          </div>

                     <div className="text-center mt-12 md:hidden">
                         <Link 
              to="/search" 
              className="inline-flex items-center bg-gradient-to-r from-red-600 to-red-700 text-white px-8 py-3 rounded-xl hover:from-red-700 hover:to-red-800 font-semibold transition-all duration-300"
            >
              View All Ads
              <ChevronRightIcon className="h-5 w-5 ml-2" />
            </Link>
           </div>
        </div>
      </section>

            {/* How It Works Section */}
      <section className="py-12 bg-gradient-to-r from-gray-50 via-white to-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">How It Works</h2>
            <p className="text-gray-600">Buy and sell in three simple steps</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            {howItWorks.map((step, index) => (
              <div key={index} className="relative group">
                <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                  {/* Step Number & Icon Container */}
                  <div className="flex items-center mb-3">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-full flex items-center justify-center text-sm font-semibold shadow-md mr-3">
                      {index + 1}
                    </div>
                    <div className={`w-10 h-10 rounded-lg ${step.color} flex items-center justify-center group-hover:scale-105 transition-transform duration-300`}>
                      <span className="text-xl">{step.icon}</span>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                  
                  {/* Connecting Arrow (except for last item) */}
                  {index < howItWorks.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-2 w-4 h-4 transform -translate-y-1/2">
                      <ChevronRightIcon className="w-4 h-4 text-red-300" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <Link 
              to="/post-ad"
              className="inline-flex items-center bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-2.5 rounded-lg hover:from-red-700 hover:to-red-800 font-semibold transition-all duration-300 shadow-md transform hover:-translate-y-1 text-sm"
            >
              Get Started
              <ChevronRightIcon className="h-4 w-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-gradient-to-br from-red-600 via-red-700 to-red-800 relative overflow-hidden">
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl font-semibold text-white mb-8">
            Ready to get started?
          </h2>
          <p className="text-xl text-red-100 mb-12 max-w-3xl mx-auto leading-relaxed">
            Join millions of satisfied users today and experience an unparalleled buying and selling experience
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
            <Link 
              to="/post-ad"
              className="bg-white text-red-600 px-10 py-4 rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 text-lg shadow-2xl transform hover:-translate-y-1"
            >
              Post Free Ad
            </Link>
            <Link 
              to="/search"
              className="bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white px-10 py-4 rounded-2xl font-semibold hover:bg-white/30 transition-all duration-300 text-lg transform hover:-translate-y-1"
            >
              Browse Ads
            </Link>
          </div>
          
          <div className="flex items-center justify-center space-x-8">
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
              <span className="text-white font-medium">Free Forever</span>
            </div>
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
              <span className="text-white font-medium">No Commission</span>
            </div>
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
              <span className="text-white font-medium">Completely Safe</span>
            </div>
          </div>
        </div>
      </section>
      {/* Cities Modal */}
      <CitiesModal
        isOpen={isCitiesModalOpen}
        onClose={closeCitiesModal}
        onConfirm={handleCitiesSelect}
        initialSelectedCities={quickSearchCities}
      />
    </main>
  );
};

export default HomePage; 
